if (!customElements.get('reviews-carousel')) {
  class ReviewsCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.viewport = this.querySelector('[data-viewport]');
      this.prevButton = this.querySelector('[data-prev]');
      this.nextButton = this.querySelector('[data-next]');
      this.source = this.dataset.source || 'blocks';
      this.productId = this.dataset.productId;
      this.perPage = Number(this.dataset.perPage) || 6;
      this.total = Number(this.dataset.total) || 0;
      this.starUrl = this.dataset.star;
      this.showDate = this.dataset.showDate === 'true';
      this.showTitle = this.dataset.showTitle === 'true';
      this.showVerified = this.dataset.showVerified === 'true';
      this.page = 1;
      this.index = 0;
      this.loading = false;
      this.done = this.source !== 'app';

      this.prevButton?.addEventListener('click', () => this.move(-1));
      this.nextButton?.addEventListener('click', () => this.move(1));
      this.viewport?.addEventListener('scroll', () => this.onViewportScroll(), { passive: true });
      this.onResize = () => this.layout();
      window.addEventListener('resize', this.onResize);

      if (this.source === 'app') this.fetchPage();
      else this.layout();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
    }

    isDesktop() {
      return window.matchMedia('(min-width: 769px)').matches;
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    async move(direction) {
      if (!this.isDesktop()) {
        const card = this.track.querySelector('.reviews-v2__card');
        const step = (card?.getBoundingClientRect().width || 300) + 12;
        this.viewport.scrollBy({ left: direction * step, behavior: 'smooth' });
        return;
      }

      const maxIndex = () => Math.max(0, this.track.children.length - 3);
      if (this.source === 'app' && direction > 0 && this.index >= maxIndex() && !this.done) {
        await this.fetchPage();
      }

      this.index = Math.min(maxIndex(), Math.max(0, this.index + direction));
      this.layout();
    }

    onViewportScroll() {
      if (this.source !== 'app' || this.isDesktop() || this.done || this.loading) return;
      const edge = this.viewport.scrollLeft + this.viewport.clientWidth;
      if (edge >= this.viewport.scrollWidth - 80) this.fetchPage();
    }

    layout() {
      if (!this.isDesktop()) {
        this.track.style.transform = '';
        this.updateButtons();
        return;
      }

      const card = this.track.querySelector('.reviews-v2__card');
      const step = card ? card.getBoundingClientRect().width + 16 : 0;
      const maxIndex = Math.max(0, this.track.children.length - 3);
      this.index = Math.min(this.index, maxIndex);
      this.track.style.transform = `translateX(${-this.index * step}px)`;
      this.updateButtons();
    }

    updateButtons() {
      if (!this.prevButton || !this.nextButton || !this.isDesktop()) return;

      const maxIndex = Math.max(0, this.track.children.length - 3);
      this.prevButton.disabled = this.index <= 0;
      this.nextButton.disabled = this.loading || (this.index >= maxIndex && this.done);
    }

    async fetchPage() {
      if (this.source !== 'app' || this.loading || this.done || !this.productId) return;

      const currentAmount = this.page * this.perPage;
      if (this.total && currentAmount > this.total && this.page > 1) {
        this.done = true;
        this.updateButtons();
        return;
      }

      this.loading = true;
      this.updateButtons();

      const url = `https://api.judge.me/reviews/reviews_for_widget?url=i7hskj-mh.myshopify.com&shop_domain=i7hskj-mh.myshopify.com&platform=shopify&page=${this.page}&per_page=${this.perPage}&product_id=${this.productId}`;

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch reviews');

        const data = await response.json();
        const htmlContent = new DOMParser().parseFromString(data.html || '', 'text/html');
        const reviews = [...htmlContent.querySelectorAll('.jdgm-rev')];

        reviews.forEach((review) => this.track.appendChild(this.buildCard(review)));

        if (!reviews.length || reviews.length < this.perPage) this.done = true;
        if (data.total_count && this.track.children.length >= Number(data.total_count)) this.done = true;
        if (this.total && this.track.children.length >= this.total) this.done = true;

        this.page += 1;

        if (!this.track.children.length) {
          this.closest('.reviews-v2')?.setAttribute('hidden', '');
        }
      } catch (error) {
        console.error(error);
        this.done = true;
      } finally {
        this.loading = false;
        this.layout();
      }
    }

    buildCard(review) {
      const score = Number(review.querySelector('.jdgm-rev__rating')?.dataset.score || 0);
      const body = review.querySelector('.jdgm-rev__body')?.innerText?.trim() || '';
      const title = review.querySelector('.jdgm-rev__title')?.innerText?.trim() || '';
      const quote = this.showTitle ? body : body || title;
      const name = review.querySelector('.jdgm-rev__author')?.innerText?.trim() || '';
      const verified = this.showVerified && review.dataset.verifiedBuyer === 'true';
      const picture = review.querySelector('.jdgm-rev__pics img')?.getAttribute('src') || '';
      const initial = (review.querySelector('.jdgm-rev__icon')?.textContent || name).trim().charAt(0);
      const date = this.showDate ? this.formatDate(review) : '';

      const card = document.createElement('article');
      card.className = 'reviews-v2__card';

      let stars = '';
      if (this.starUrl) {
        for (let i = 1; i <= 5; i += 1) {
          stars += `<img class="reviews-v2__star${i > score ? ' is-off' : ''}" src="${this.escapeHtml(this.starUrl)}" alt="" width="16" height="16">`;
        }
      }

      const avatar = picture
        ? `<img class="reviews-v2__photo" src="${this.escapeHtml(picture)}" alt="">`
        : `<span class="reviews-v2__initial" aria-hidden="true">${this.escapeHtml(initial)}</span>`;

      const quoted = quote ? `“${this.escapeHtml(quote)}”` : '';
      const titleHtml = this.showTitle && title
        ? `<p class="reviews-v2__review-title">${this.escapeHtml(title)}</p>`
        : '';
      const dateHtml = date ? `<p class="reviews-userinfo__date">${this.escapeHtml(date)}</p>` : '';
      const verifiedHtml = verified ? `<p class="reviews-v2__verified">Verified buyer</p>` : '';

      card.innerHTML = `
        <div class="reviews-v2__card-top">
          ${stars ? `<div class="reviews-v2__stars" role="img" aria-label="${score} out of 5">${stars}</div>` : ''}
          ${titleHtml}
          ${quoted ? `<p class="reviews-v2__quote">${quoted}</p>` : ''}
        </div>
        <div class="reviews-v2__author">
          ${avatar}
          <div class="reviews-v2__person">
            ${verifiedHtml}
            <p class="reviews-v2__name">${this.escapeHtml(name)}</p>
            ${dateHtml}
          </div>
        </div>
      `;

      return card;
    }

    formatDate(review) {
      const timestamp = review.querySelector('.jdgm-rev__timestamp');
      const raw = timestamp?.getAttribute('datetime') || timestamp?.dataset.content || '';
      if (!raw) return '';

      const parsed = new Date(raw);
      if (Number.isNaN(parsed.getTime())) return '';

      return new Intl.DateTimeFormat('en-US').format(parsed);
    }
  }

  customElements.define('reviews-carousel', ReviewsCarousel);
}
